
import { Component, Vue, Prop } from 'vue-property-decorator'
import { ClientStatus, Workspace, ClientWorkspacesFilters, Domain, BLUEMESSAGING_ID, DomainType } from '@/types/Catalogs'
import DateSelector from '@/components/generic/DateSelector.vue'
import LoadingMask from '@/components/generic/LoadingMask.vue'
import catalogsService from '@/services/CatalogsService'
import { PermissionNames, RoleNames } from '@/types/Auth'
import userInfoStore from '@/store/userInfo'
import { PageRequest, PageResponse } from '@/types/Support'
import MessageService from '@/services/support/MessageService'
import reportsService from '@/services/ReportsService'
import { MessageError, MessageSuccess } from '@/components/generic/MessageHandling.vue'
import LogService, { ToString } from '@/services/support/LogService'
import { NUMBER } from '@/types/GeneralConstants'

@Component({
  components: {
    DateSelector,
    LoadingMask
  }
})
export default class AdminWorkspaces extends Vue {
  @Prop({ required: true, default: 0 })
  id: number

  @Prop({ required: true, default: ClientStatus.APROBACION })
  status: ClientStatus

  @Prop({ required: true, default: '' })
  title: number

  @Prop({ required: true, default: [] })
  domains: Domain[]

  @Prop({ required: false, default: [] })
  allWorkspaces: Workspace[]

  @Prop({ required: false, default: false })
  autoForm: boolean

  @Prop({ required: false, default: false })
  readOnly: number

  MAX_WORKSPACES_FOR_INDIVIDUAL_DOMAIN = 2
  PAGE_SIZE = 500
  formOpen = false
  domainSelectionOpen = false
  loading = false
  domainSelected: any = {}
  domainSelectedBackup: any = {}
  workspaces: Workspace[] = []
  workspaceSelected = new Workspace()
  workspacesStatus = [
    { value: 'active', text: 'Activo', disabled: true },
    { value: 'inactive', text: 'Baja', disabled: true },
    { value: 'suspended', text: 'Suspendido', disabled: true }
  ]
  domainSelectedWorkspaces: PageResponse<Workspace> = new PageResponse()
  domainsAndClients: Map<number, number[]> = new Map()
  availableDomainIds: number[] = []
  searchDomain = ''
  form = Workspace.getIntanceForEdit()

  regexPhone = /^[+]?[0-9]{10,15}$/

  detailReport: number = -1
  monthReport: number = -1

  /**
   * Gets the name of the month corresponding to the given index.
   * @param {number} monthIndex - Index of the month (0 for January, 11 for December).
   * @returns {string} Name of the month in Spanish.
   */
  getMonthName (monthIndex: number) {
    const monthNames = [
      'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
      'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
    ]
    return monthNames[monthIndex]
  }

  /**
   * Gets the name of the current month.
   * @returns {string} Name of the current month in Spanish.
   */
  currentMonthName () {
    return this.getMonthName(new Date().getMonth())
  }

  /**
   * Gets the name of the previous month.
   * @returns {string} Name of the previous month in Spanish.
   */
  previousMonthName () {
    let currentMonth = new Date().getMonth()
    let previousMonth = currentMonth === 0 ? 11 : currentMonth - 1
    return this.getMonthName(previousMonth)
  }

  showDisplayModal () {
    this.$bvModal.show('report-support-billing')
  }

  closeDisplayModal () {
    this.detailReport = -1
    this.monthReport = -1
    this.$bvModal.hide('report-support-billing')
  }

  /**
   * Sending a request to the server to generate a report.
   */
  solicitarReport () {
    reportsService
      .postSupportBilling(
        this.detailReport,
        this.userEmail,
        this.id,
        this.workspaceSelected.domain.id,
        this.monthReport
      )
      .then((response) => {
        let message = `En los próximos 10 minutos recibirá un correo con el reporte, cualquier duda por favor comunícate a soporte.`
        let messageValues = {
          title: 'Generando reporte.',
          message: message
        }
        new MessageSuccess({ propsData: messageValues }).displaMessage()
      })
      .catch((error) => {
        let messageValues = {
          title: 'Surgio un error contacte a soporte',
          message: '',
          delay: 10000,
          error: error
        }
        new MessageError({ propsData: messageValues }).displayMessage()
      })
      .finally(() => {
        this.closeDisplayModal()
      })
  }

  mounted () {
    this.init()
    if (this.autoForm) {
      this.formOpen = true
    }
  }

  get createPermission () {
    return userInfoStore.userInfo.role.permissions.some(it => it.name === PermissionNames.WORKSPACES_CREATE)
  }

  get updatePermission () {
    return userInfoStore.userInfo.role.permissions.some(it => it.name === PermissionNames.WORKSPACES_UPDATE)
  }

  get userHasFinancesAdminRole () {
    return userInfoStore.hasRole(RoleNames.FINANCES_ADMIN)
  }

  get isClientActive () {
    return this.status === ClientStatus.ACTIVO
  }

  /**
   * Returns the email property of the userInfo object.
   */
  get userEmail () {
    return userInfoStore.userInfo.email
  }

  init () {
    this.workspaces = []
    const pageRequest = new PageRequest<ClientWorkspacesFilters>(this.PAGE_SIZE)
    pageRequest.filters.clientId = this.id
    this.loadWorkspacesPage(pageRequest)
      .then(() => {
        this.domainsAndClients = this.createDomainAndClientMap(this.allWorkspaces)
        this.loadAvailableDomains()
      })
  }

  newItem () {
    this.workspaceSelected = Workspace.getIntanceForEdit()
    this.form = this.workspaceSelected
    this.searchDomain = ''
    this.showForm(true)
    setTimeout(() => {
      document.getElementById('input-domain')!.focus()
    }, 500)
  }

  updateItem () {
    this.form = JSON.parse(JSON.stringify(this.workspaceSelected))
    const domain = this.domains.find((it: Domain) => it.id === this.workspaceSelected.domain.id) as any
    this.selectDomain(domain)
    this.showForm(true)
  }

  showForm (flag: boolean) {
    if (flag) {
      this.domainSelectedBackup = {}
      this.formOpen = true
    } else {
      this.formOpen = false
    }
  }

  showDomainSelection (flag: boolean) {
    if (flag) {
      this.searchDomain = ''
      this.domainSelected = {}
      this.domainSelectionOpen = true
    } else {
      setTimeout(() => {
        this.domainSelectionOpen = false
        if (this.domainSelectedBackup.code) {
          this.selectDomain(this.domainSelectedBackup)
        }
      }, 200)
    }
  }

  async loadWorkspacesPage (pageRequest: PageRequest<any>) {
    await catalogsService.getWorkspacesByClient(pageRequest).then(response => {
      this.workspaces.push(...response.results)

      if (this.workspaces.length < response.total) {
        pageRequest.page++
        this.loadWorkspacesPage(pageRequest)
      } else {
        this.$emit('workspacesLoaded', this.workspaces)
      }
    }).catch((error) => {
      LogService.error(`Error en la carga de Workspaces: ${ToString(error.response ?? error)}`)
    })
  }

  /**
   * Based on a list of workspaces create a Map with the form:
   * : Key - domainId
   * : Value - [clientIds]; this are all the clients related to a domain
   * @returns a Map with the info of each domain; type and clients
   */
  createDomainAndClientMap (workspaces: Workspace[]): Map<number, number[]> {
    const map = new Map<number, number[]>()
    workspaces.forEach(workspace => {
      let currentDomainId = workspace.domain.id
      let currentClientId = workspace.client.id
      if (map.has(currentDomainId)) {
        map.get(currentDomainId)!.push(currentClientId)
      } else {
        map.set(currentDomainId, [currentClientId])
      }
    })
    return map
  }

  /**
   * Set which domains would be able to select
   */
  async loadAvailableDomains () {
    this.domainsAndClients.forEach((clientIds, domainId) => {
      let domain = this.domains.find(domain => domain.id === domainId)
      // We add all the Ecosystm domains
      if (domain && domain.domainType === DomainType.ECOSYSTEM) {
        this.availableDomainIds.push(domain!.id)
      } else { // Otherwise is an Individual domain
        if (clientIds.length < this.MAX_WORKSPACES_FOR_INDIVIDUAL_DOMAIN) { // It has zero or one client
          if (clientIds.length === 0) {
            this.availableDomainIds.push(domain!.id)
          } else if ((clientIds.includes(BLUEMESSAGING_ID) && this.id !== BLUEMESSAGING_ID) ||
          (!clientIds.includes(BLUEMESSAGING_ID) && this.id === BLUEMESSAGING_ID)) {
            this.availableDomainIds.push(domain!.id)
          }
        }
      }
    })
    // Add all the domains that don't have workspaces
    this.domains.forEach(domain => {
      if (!this.domainsAndClients.has(domain.id)) this.availableDomainIds.push(domain.id)
    })
    // Delete items duplicated
    this.availableDomainIds = Array.from(new Set<number>(this.availableDomainIds))
  }

  /**
   * Validate which domain can be selected
   *
   * @param domain to validate
   */
  isMatchDomain (domain: Domain) {
    if (this.workspaces.length) {
      // Hide all domains that are already related to the client
      for (let workspace of this.workspaces) {
        if (workspace.domain.id === domain.id) return false
      }
    }
    // Hide all domains that can be selected
    if (!this.availableDomainIds.includes(domain.id)) return false

    if (!this.searchDomain) return true

    // Match the client name
    const regexSearch = new RegExp(this.searchDomain, 'i')
    return regexSearch.test(domain.name)
  }

  async selectDomain (domain: Domain) {
    this.domainSelected = domain
    this.domainSelectedBackup = domain
    this.searchDomain = domain.name
    this.domainSelectedWorkspaces = await this.firstTenWorkspacesByDomain(domain.id)
  }

  getWorkspaceStatusDescription (status: string) {
    return Workspace.getStatusDescription(status)
  }

  validateState (ref: any) {
    const thiz = this as any
    if (
      thiz.veeFields[ref] &&
      (thiz.veeFields[ref].dirty || thiz.veeFields[ref].validated)
    ) {
      return !thiz.veeErrors.has(ref)
    }
    return null
  }

  async firstTenWorkspacesByDomain (domainId: number): Promise<PageResponse<Workspace>> {
    const request = new PageRequest<ClientWorkspacesFilters>()
    request.size = 10
    request.filters.domainId = domainId
    const response = await catalogsService.getWorkspacesByDomain(request)
    return response
  }

  async onSubmit () {
    if (!this.domainSelected.id) {
      MessageService.error('Validación de formulario', 'Debe elegir un dominio', 3000)
      return
    }
    this.form.client.id = this.id
    this.form.domain.id = this.domainSelected.id

    const requiredFieldsFillUp = await this.$validator.validateAll()
    if (requiredFieldsFillUp) {
      this.loading = true

      // If this workspaces created is the first for the domain, we set it as owner
      if (this.domainSelectedWorkspaces.total === NUMBER.ZERO) {
        // Exception when Bluemessaging will be the owner
        if (!this.userHasFinancesAdminRole && this.form.client.id === BLUEMESSAGING_ID) {
          this.$bvToast.toast(
            'Lo sentimos, no puede asociar a Bluemessaging como owner del dominio. Por favor, seleccione un dominio diferente o contacte al Gerente de Fiananzas.',
            {
              title: 'Asignación no autorizada.',
              variant: 'danger',
              autoHideDelay: 6000
            }
          )
          this.loading = false
          return
        }
        this.form.owner = true
      }

      catalogsService.saveWorkspace(this.form).then((response: Workspace) => {
        this.init()
        this.showForm(false)
        this.$emit('update-status', ClientStatus.ACTIVO)
        if (this.autoForm) {
          this.$emit('close')
        }
        this.$bvToast.toast(
          'El Workspace se agregado correctamente.',
          {
            title: 'Workspace completado.',
            variant: 'success',
            autoHideDelay: 6000
          }
        )
      }).catch((error) => {
        new MessageError({
          propsData: {
            title: 'Error al guardar.',
            message: '',
            delay: 10000,
            error: error } }).displayMessage()
      }).finally(() => {
        this.loading = false
      })
    }
  }

  onCreateDomain () {
    this.$emit('create-domain')
  }

  onCancel () {
    this.$emit('close')
  }
}
